import Image from "@Components/Image";
import VideoPlayer from "../GalleryModal/components/VideoPlayer/VideoPlayer";
import { getVideoSrc, getVideoThumb } from "../SectionFeatured.tsx/utils";
import getImagePath from "@Utils/getImagePath";
import CarouselTitle from "./components/CarouselTitle";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useEffect, useMemo, useState } from "react";
import { getAvailableStartingPricing, getPricingDuration } from "../RoomTypeSection/utils";
import Carousel from "@Components/Carousel";
import classes from "./GalleryCarousel.module.scss";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import WhiteStarIcon from "@Icons/WhiteStarIcon";
import ThreeDView from "../GalleryModal/components/ThreeDView/ThreeDView";
import ReviewCaption from "../ReviewsSection/Components/ReviewCaption";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import { AppState } from "@Store/index";
import useTranslationHook from "hooks/useTranslationHook";
import { PAGE } from "constants/configConstants";
import { useHistory } from "hooks/useHistory";
import viewTransitionNavUtility from "@Utils/viewTransitionNavUtility";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import LeadFormDispatcher from "@Components/LeadFormModal/actions";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
//

/* eslint-disable jsx-a11y/iframe-has-title */
const Media = ({
  category,
  index,
  item,
  initialItemIndex,
  autoPlay,
  isCurrentSlide,
  withWatermark = true,
}: any) => {
  const isDesktop = useIsDesktop();
  const imgModifier = isDesktop ? "gallery-full-screen-desktop" : "gallery-full-screen-mobile";
  switch (category) {
    case "3D":
      return <ThreeDView key={index} item={item} events="auto" />;
    case "videos":
      return (
        <VideoPlayer
          src={getVideoSrc(item)}
          thumbnailUrl={getVideoThumb(item)}
          isYoutube={item?.path?.includes("youtube")}
          autoPlay={autoPlay && isCurrentSlide}
          fromGalleryCarousel
        />
      );
    default:
      return (
        <Image
          width="100%"
          height="100%"
          loading={initialItemIndex === index ? "eager" : "lazy"}
          alt={item.caption || "Property image"}
          src={getImagePath(item.base_path || item.path, imgModifier, {}, withWatermark)}
          className={classes.carouselItem}
        />
      );
  }
};

const GalleryCarousel = ({
  currentCategoryItems,
  initialItemIndex,
  currentCategory,
  showViewRoomButton = true,
  galleryCarouselReviewSection = false,
  showShare = true,
  isDesktop = false,
  onClose,
  // false when the property's meta.is_watermark_disabled is true (P1D-5542)
  withWatermark = true,
}: any) => {
  const customWindowOpen = useCustomWindowOpen();
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const [canAutoPlay, setCanAutoPlay] = useState(false);
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const { data, reviewsection, isLFOneFilled, roomID } = useSelector(
    ({ propertyPageData, leadFormModal }: AppState) => ({
      data: propertyPageData.data,
      reviewsection: propertyPageData.gallery_carousel.reviewSection,
      roomID: propertyPageData.gallery_carousel.config_id,
      isLFOneFilled: leadFormModal.isLFOneFilled,
    }),
    shallowEqual,
  );

  const showBookButton = false;
  const isHappyFlow = useIsHappyFlow();

  const formatCategoryItems = () => {
    if (currentCategory === "site_plan") {
      const newCategoryItems: any = [];
      currentCategoryItems.forEach((item: any) => {
        if (!item?.floor_details) return;
        item.floor_details.forEach((floor: any) => {
          newCategoryItems.push({
            base_path: floor.url,
            caption: `Building ${item.building} - Floor${floor.floor}`,
            category: "images",
            path: floor.url,
            thumbnail_url: floor.url,
          });
        });
      });
      return newCategoryItems;
    }
    return currentCategoryItems;
  };
  const _currentCategoryItems = useMemo(
    () => formatCategoryItems(),
    [currentCategory, currentCategoryItems],
  );

  const [itemIndex, setItemIndex] = useState(initialItemIndex + 1);
  const [configName, setConfigName] = useState(
    _currentCategoryItems[initialItemIndex]?.caption ||
      _currentCategoryItems[initialItemIndex]?.configName ||
      "",
  );
  const [configData, setConfigData] = useState({
    name: "",
    available: true,
    pricing: { duration: "" },
    level: "",
  });

  const pricingPrice = getAvailableStartingPricing(configData?.pricing);
  const pricingDuration = getPricingDuration(configData?.pricing?.duration);

  const isRoomLevel = configData?.level === "child";

  const shouldShowBookButton = showBookButton && !reviewsection && currentCategory != "3D";

  const history = useHistory();

  useEffect(() => {
    if (
      _currentCategoryItems[initialItemIndex]?.level === "child" ||
      currentCategory === "room-type"
    ) {
      let inv;
      const currentItem = _currentCategoryItems[initialItemIndex];

      // For 3D views, find by configName
      if (currentItem?.category === "3D" && currentItem?.configName) {
        inv = data?.children_short?.find((child: any) => child.name === currentItem?.configName);
      } else {
        // For images, find by caption or roomID
        inv = data?.children_short?.find((child: any) => {
          if (child?.images?.length)
            return child?.images?.find((image: any) => image?.caption === currentItem?.caption);
          return child.id === roomID;
        });
      }

      setConfigData({
        ...inv,
        imageType: currentItem?.type,
        level: currentItem?.level || "child",
      });
    } else {
      setConfigData(data);
    }
  }, [_currentCategoryItems]);

  const onSlideChange = (idx: any) => {
    setCanAutoPlay(false); // Disable autoplay immediately

    if (_currentCategoryItems[idx - 1]?.level === "child" || currentCategory === "room-type") {
      let inv;
      const currentItem = _currentCategoryItems[idx - 1];

      // For 3D views, find by configName
      if (currentItem?.category === "3D" && currentItem?.configName) {
        inv = data?.children_short?.find((child: any) => child.name === currentItem?.configName);
      } else {
        // For images, find by caption
        inv = data?.children_short?.find((child: any) =>
          child?.images?.find((image: any) => image?.caption === currentItem?.caption),
        );
      }

      setConfigData({
        ...inv,
        imageType: currentItem?.type,
        level: currentItem?.level || "child",
      });
    } else {
      setConfigData(data);
    }
    setItemIndex(idx);
    setConfigName(
      _currentCategoryItems[idx - 1]?.caption || _currentCategoryItems[idx - 1]?.configName || "",
    );

    // Only enable autoplay for video slides after a delay
    const currentItem = _currentCategoryItems[idx - 1];
    const isVideoSlide = currentItem?.category === "videos" || currentCategory === "videos";

    if (isVideoSlide) {
      setTimeout(() => {
        setCanAutoPlay(true);
      }, 500); // Slightly longer delay for videos
    }
  };

  useEffect(() => {
    // Delay autoplay to prevent rapid triggers when sliding
    const timer = setTimeout(() => {
      setCanAutoPlay(true);
    }, 300); // 300ms delay

    return () => {
      clearTimeout(timer);
      setCanAutoPlay(false);
    };
  }, [itemIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleCloseModal(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleCloseModal = (shouldCloseSingleModal = false) => {
    window.history.replaceState(window.history.state, "", window.location.pathname);
    propertyPageDispatcher.showGalleryCarousel({
      show: false,
      images: [],
      category: "",
      currentItemIndex: 0,
      showViewRoomButton: true,
    });
    if (!shouldCloseSingleModal)
      propertyPageDispatcher.showGalleryModal({ show: false, selectedTab: "all", data: {} });
  };

  const handleOnClick = () => {
    gtagEventEmitter({
      action: "clicked_view_room",
      category: "inventory_page",
      label: "photo_section",
    });
    handleCloseModal();
    // Close reviews panel if open
    window.dispatchEvent(new CustomEvent("close-reviews-panel"));
    // Delay scroll to allow modal/panel close animations and DOM re-render to settle
    setTimeout(() => {
      const element = document.getElementById("room_type");
      if (!element) return;
      const headerOffset = 54 + 48; // padding plus navbar height
      const elementPosition = element?.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }, 300);
  };

  const tenancyBookHandler = () => {
    const currConfigName = configData?.level === "child" ? configData.name : "";
    gtagEventEmitter({
      action: "clicked_gallery_book_now_button",
      category: PAGE.INVENTORY,
      source: showViewRoomButton ? "gallery_room_type" : "gallery",
    });

    handleCloseModal();
    if (!isLFOneFilled || !isHappyFlow || !configData?.available) {
      const leadFormDispatcher = new LeadFormDispatcher(dispatch);
      leadFormDispatcher.handleOnEnquireClick({
        data: {
          ...configData,
          region: data.region,
          canonical_name: data.canonical_name,
          ...(_currentCategoryItems[itemIndex]?.level === "child" && {
            level: _currentCategoryItems[itemIndex]?.level,
          }),
        },
        source: "clicked_gallery_book_now_button",
        page: 1,
      });
    } else if (configData?.available) {
      if (isHappyFlow)
        viewTransitionNavUtility(
          () =>
            history.push(
              currConfigName
                ? `/places/${data.canonical_name}/form?config_name=${currConfigName}`
                : `/places/${data.canonical_name}/form`,
            ),
          "right-to-left",
        );
      else
        customWindowOpen({
          url: currConfigName
            ? `/places/${data.canonical_name}/form?config_name=${currConfigName}`
            : `/places/${data.canonical_name}/form`,
          target: "_blank",
        });
    }
  };
  const slideList = useMemo(
    () =>
      _currentCategoryItems
        .filter((item: any) => item != null)
        .map((item: any, index: any) => {
          const isCurrentSlide = itemIndex === index + 1;
          const category =
            currentCategory === "shot_by_residents" ||
            currentCategory === "site_plan" ||
            currentCategory === "room-type"
              ? item.category
              : currentCategory || item.category;

          return (
            <Media
              key={`slide-${item.path || item.base_path || item.caption}-${index}`}
              category={category}
              index={index}
              initialItemIndex={initialItemIndex}
              item={item}
              // CRITICAL: Only autoplay if it's the current slide AND can autoplay
              autoPlay={category === "videos" && canAutoPlay && isCurrentSlide}
              isCurrentSlide={isCurrentSlide}
              withWatermark={withWatermark}
            />
          );
        }),
    [
      _currentCategoryItems,
      currentCategory,
      initialItemIndex,
      itemIndex,
      canAutoPlay,
      withWatermark,
    ],
  );

  useEffect(() => {
    // Pause all video elements that are not the current slide
    const allVideos = document.querySelectorAll("video");
    allVideos.forEach((video) => {
      // If this video is not in the current slide, pause it
      if (!video.paused) {
        const videoContainer = video.closest("[data-slide-index]");
        if (videoContainer) {
          const slideIndex = parseInt(videoContainer.getAttribute("data-slide-index") || "0", 10);
          if (slideIndex !== itemIndex - 1) {
            video.pause();
            video.currentTime = 0; // Optional: reset to beginning
          }
        }
      }
    });
  }, [itemIndex]);

  return (
    <div className={classes.container}>
      <CarouselTitle
        currentItemIndex={itemIndex}
        totalItemCount={_currentCategoryItems.length}
        galleryCarouselReviewSection={galleryCarouselReviewSection}
        showShare={showShare}
        currentCategory={currentCategory}
        onClose={onClose}
      />
      <Carousel
        className={formatClassNames(classes.carouselContainer, isDesktop && classes.desktop)}
        leftNavSliderClassname={isDesktop && classes.carouselContainerLeftArrow}
        rightNavSliderClassname={isDesktop && classes.carouselContainerRightArrow}
        activePos={itemIndex}
        handleChangeSlide={onSlideChange}
        slideList={slideList}
        showNavSlider={isDesktop || currentCategory === "3D" || currentCategory === "room-type"}
        arrowColor={isDesktop ? "black" : "#fff"}
        keypressEnabled
      />

      <div className={formatClassNames(classes.footer, isDesktop && classes.desktop)}>
        <div className={classes.footerSubSection}>
          <div data-testid="property-image-caption" className={classes.footerTitle}>
            {(() => {
              const currentItem = _currentCategoryItems[itemIndex - 1];
              if (currentItem?.showCaption && galleryCarouselReviewSection) {
                return currentItem.userName;
              }
              if (currentItem?.showCaption) {
                return (
                  <ReviewCaption
                    userName={currentItem.userName}
                    anonymous={currentItem.anonymous}
                    rating={currentItem.rating}
                  />
                );
              }
              return t(configName);
            })()}
          </div>
          {isRoomLevel && pricingDuration && (
            <div className={classes.footerSubTitle}>
              {t("propertypage.components.gallerycarousel.startsfrom")} {pricingPrice}/
              {pricingDuration}
            </div>
          )}
        </div>
        {galleryCarouselReviewSection ? (
          <div className={classes.ratingDivContainer}>
            <div className={classes.ratingDiv}>
              {_currentCategoryItems[itemIndex - 1]?.rating} <WhiteStarIcon />
            </div>
            {_currentCategoryItems[itemIndex - 1]?.roomTypeName && (
              <button
                data-testid="review-gallery-view-room"
                type="button"
                onClick={handleOnClick}
                className={classes.button}
                style={{ marginLeft: "16px" }}
              >
                {t("propertypage.components.gallerycarousel.viewroom")}
              </button>
            )}
          </div>
        ) : (
          ""
        )}
        {shouldShowBookButton ? (
          <button
            data-testid="Book Now"
            type="button"
            onClick={() => tenancyBookHandler()}
            className={classes.button}
          >
            {t("propertypage.components.gallerycarousel.booknow")}
          </button>
        ) : (
          <></>
        )}
        {showViewRoomButton && (!showBookButton || currentCategory === "3D") && !reviewsection ? (
          <button
            data-testid={
              isRoomLevel
                ? t("propertypage.components.gallerycarousel.viewroom")
                : t("propertypage.components.gallerycarousel.viewrooms")
            }
            type="button"
            onClick={handleOnClick}
            className={classes.button}
          >
            {isRoomLevel
              ? t("propertypage.components.gallerycarousel.viewroom")
              : t("propertypage.components.gallerycarousel.viewrooms")}
          </button>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(GalleryCarousel, {
  componentName: "GalleryCarousel-PropertyPage",
  showForChina: true,
});
